import {
  Box,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDesignSourceReport, useDesignAnalytics } from "./api/designSourceReportApi";
import { formatDate } from "../../utils/convertDate";
import { getIcon } from "../../utils/iconConfig";
import ChildHeaderCard from "../../components/ui/card-templates/ChildHeaderCard";
import TabSkeleton from "../../components/ui/TabSkeleton";
import DatePickerComp from "../../components/ui/react-DatePicker/DatePickerComp";
import { ReactComponent as Calendar } from "../../assets/child-header-icons/calendar.svg";
import { ReactComponent as WhiteDownArrow } from "../../assets/icons/whiteDropdown.svg";
import { DESIGN_SECTIONS } from "../cart/api/designStats";
import { getClientLabel } from "../../utils/clientLabel";
import "../../components/ui/search/newSearchStyle.css";
import apiClient from "../../lib/api_client";
import { capitalEachLetter } from "../../utils/capitalEachLetter";

// yyyy-mm-dd for the API, same as the native date input sent before.
const toApiDate = (date) => (date ? date.toISOString().slice(0, 10) : undefined);

// Columns shown on screen, each folding in one or more of the sections the
// server counts separately. Relevant Set and Recommended Designs are both
// browsed off a category rail, same as the category browser itself, so their
// counts land under Categories rather than getting a column of their own.
// "Other" - a design with no real section - has no column at all.
const SECTION_GROUPS = [
  {
    label: DESIGN_SECTIONS.CATEGORIES,
    sources: [
      DESIGN_SECTIONS.CATEGORIES,
      DESIGN_SECTIONS.RELEVANT_SET,
      DESIGN_SECTIONS.RECOMMENDED,
    ],
  },
  { label: DESIGN_SECTIONS.SEARCH, sources: [DESIGN_SECTIONS.SEARCH] },
  { label: DESIGN_SECTIONS.SCAN, sources: [DESIGN_SECTIONS.SCAN] },
  { label: DESIGN_SECTIONS.COLLECTIONS, sources: [DESIGN_SECTIONS.COLLECTIONS] },
];

// Sums a group's sources out of a { section: count } map - summary.bySection
// for the header chips, row.sectionCounts for a table row.
const groupTotal = (counts, group) =>
  group.sources.reduce((total, sec) => total + (counts?.[sec] || 0), 0);

const COLUMNS = [
  { key: "clientName", label: "Client Name" },
  { key: "date", label: "Date" },
  { key: "eventName", label: "Event" },
  { key: "mobile", label: "Mobile Number" },
  { key: "email", label: "Email Address" },
  { key: "designQty", label: "Design Qty" },
  { key: "totalDiamondWt", label: "Diamond Wt" },
  { key: "totalGoldWt", label: "Gold Wt" },
  ...SECTION_GROUPS.map((group) => ({
    key: `section:${group.label}`,
    label: group.label,
  })),
  { key: "salesPerson", label: "Sales Person" },
];

const formatMobile = (number) => {
  if (!number) return "-";
  const str = number.toString();
  if (str.startsWith("91") && str.length > 10) return `+91 ${str.slice(2)}`;
  if (str.length <= 10) return `+91 ${str}`;
  return str;
};

// Same table look as the Orders screen - dark header row, thin white grid,
// scrollbar tucked to the same width.
const StyledTableContainer = styled(TableContainer)(() => ({
  backgroundColor: "transparent",
  flex: "0 1 auto",
  minHeight: 0,
  overflowX: "auto",
  border: ".05vw solid #fff",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { height: ".3vw" },
  "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: ".15vw" },
}));

const StyledTable = styled(Table)(() => ({
  "& thead": {
    backgroundColor: "#444444",
    position: "sticky",
    top: 0,
    zIndex: 1,
    "& th": {
      fontSize: ".7vw",
      fontWeight: 400,
      lineHeight: "1.1vw",
      color: "#FFFFFF",
      textAlign: "center",
      fontFamily: "Poppins",
      padding: ".5vw",
      borderBottom: ".05vw solid #fff",
    },
  },
  "& tbody td": {
    fontSize: ".7vw",
    fontWeight: 400,
    lineHeight: "1vw",
    color: "#FFFFFF",
    textAlign: "center",
    fontFamily: "Poppins",
    borderBottom: ".05vw solid #fff",
    padding: ".5vw .25vw",
    border: ".05vw solid #fff",
  },
}));

const TABS = ["Overview", "By Client", "By State", "By City", "By Category"];

const StyledTab = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  padding: ".35vw 1vw",
  cursor: "pointer",
  fontFamily: "Poppins",
  fontSize: ".75vw",
  fontWeight: active ? 600 : 400,
  color: active ? "#fff" : "#888",
  borderBottom: active ? ".15vw solid #50C878" : ".15vw solid transparent",
  whiteSpace: "nowrap",
  transition: "color .15s, border-color .15s",
}));

const AnalyticsTable = ({ columns, rows, loading, emptyMsg }) => (
  <StyledTableContainer>
    <StyledTable>
      <TableHead>
        <TableRow>
          {columns.map((c) => <TableCell key={c.key}>{c.label}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {loading &&
          Array.from({ length: 8 }).map((_, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={c.key}>
                  <Skeleton variant="text" animation="wave" sx={{ fontSize: "1rem" }} />
                </TableCell>
              ))}
            </TableRow>
          ))}
        {!loading && rows.length === 0 && (
          <TableRow>
            <TableCell colSpan={columns.length}>{emptyMsg}</TableCell>
          </TableRow>
        )}
        {!loading && rows.map((row, i) => (
          <TableRow key={i}>
            {columns.map((c) => {
              let val = row[c.key] ?? "-";
              if (val !== "-") {
                if (c.key === "totalDiamondWt") val = `${val} ct`;
                else if (c.key === "totalGoldWt") val = `${val} g`;
              }
              return <TableCell key={c.key}>{val}</TableCell>;
            })}
          </TableRow>
        ))}
      </TableBody>
    </StyledTable>
  </StyledTableContainer>
);

const ReportScreen = () => {
  const [draftStart, setDraftStart] = useState(null);
  const [draftEnd, setDraftEnd] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [filterEvent, setFilterEvent] = useState("");
  const [eventOptions, setEventOptions] = useState([]);

  useEffect(() => {
    apiClient.post("/getAllEventType", { pageParam: 1, limit: 1000 })
      .then((res) => setEventOptions(res?.data?.eventTypeData || []))
      .catch(() => {});
  }, []);

  const { data, isLoading } = useDesignSourceReport({
    startDate: toApiDate(startDate),
    endDate: toApiDate(endDate),
    search: search || undefined,
    eventTypeId: filterEvent || undefined,
  });

  const { data: analytics, isLoading: analyticsLoading } = useDesignAnalytics({
    startDate: toApiDate(startDate),
    endDate: toApiDate(endDate),
    search: search || undefined,
    eventTypeId: filterEvent || undefined,
  });

  useEffect(() => { return () => setSearch(""); }, []);

  const rows = data?.rows || [];
  const summary = data?.summary || {};

  const handleDateRangeChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
  };

  const handleDateClear = () => {
    setDraftStart(null);
    setDraftEnd(null);
    setStartDate(null);
    setEndDate(null);
  };

  const handleDateApply = () => {
    setStartDate(draftStart);
    setEndDate(draftEnd);
  };

  const runSearch = () => setSearch(searchText.trim());
  const clearSearch = () => { setSearchText(""); setSearch(""); };

  const summaryCards = SECTION_GROUPS.map((group) => ({
    key: `${group.label} : `,
    value: `${groupTotal(summary.bySection, group)}`,
  }));

  const clientColumns = [
    { key: "clientName", label: "Client Name" },
    { key: "orders", label: "Orders" },
    { key: "designs", label: "Design Qty" },
    { key: "totalDiamondWt", label: "Diamond Wt" },
    { key: "totalGoldWt", label: "Gold Wt" },
  ];

  const stateColumns = [
    { key: "state", label: "State" },
    { key: "orders", label: "Orders" },
    { key: "designs", label: "Design Qty" },
    { key: "totalDiamondWt", label: "Diamond Wt" },
    { key: "totalGoldWt", label: "Gold Wt" },
  ];

  const cityColumns = [
    { key: "city", label: "City" },
    { key: "orders", label: "Orders" },
    { key: "designs", label: "Design Qty" },
    { key: "totalDiamondWt", label: "Diamond Wt" },
    { key: "totalGoldWt", label: "Gold Wt" },
  ];

  const categoryColumns = [
    { key: "category", label: "Category" },
    { key: "designs", label: "Design Qty" },
    { key: "pieces", label: "Pieces" },
    { key: "totalDiamondWt", label: "Diamond Wt" },
    { key: "totalGoldWt", label: "Gold Wt" },
  ];

  return (
    <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Header bar */}
      <Box
        sx={{
          width: "100%",
          flexShrink: 0,
          background: "#000000",
          padding: "1vw 1.6vw",
          display: "flex",
          alignItems: "center",
          gap: "1.2vw",
          overflowX: "auto",
          flexWrap: "nowrap",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { height: ".3vw" },
          "&::-webkit-scrollbar-thumb": { background: "#888", borderRadius: ".15vw" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: ".4vw", flexShrink: 0 }}>
          {getIcon("Score")}
          <Typography fontFamily="Poppins" fontSize="1vw" fontWeight={500} whiteSpace="nowrap">
            Reports
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: ".6vw", flexShrink: 0 }}>
          {isLoading ? (
            <TabSkeleton height="2.2vw" width="10.5vw" count={SECTION_GROUPS.length} />
          ) : (
            summaryCards.map((card, index) => (
              <ChildHeaderCard key={index} icon={card.icon} title={card.key} value={card.value} width="10.5vw" />
            ))
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: "1vw", flexShrink: 0, marginLeft: "auto" }}>
          <Select
            displayEmpty
            value={filterEvent}
            onChange={(e) => setFilterEvent(e.target.value)}
            IconComponent={WhiteDownArrow}
            renderValue={(selected) => {
              if (!selected) return <span style={{ color: "#EDEDEB" }}>All Events</span>;
              const ev = eventOptions.find((e) => e._id === selected);
              return ev ? capitalEachLetter(ev.eventTypeName) : "";
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  border: ".025vw solid #595959",
                  borderRadius: ".75vw",
                  backgroundColor: "#FFFFFF",
                  minWidth: "10vw",
                  fontSize: ".8vw",
                  fontFamily: "Poppins",
                  padding: ".25vw",
                  maxHeight: "9.3vw",
                  overflowY: "scroll",
                  "& .MuiList-root": { paddingTop: 0, paddingBottom: 0 },
                  "& .MuiMenuItem-root": {
                    fontFamily: "Karla",
                    fontWeight: 500,
                    fontSize: ".9vw",
                    "&.Mui-selected": { backgroundColor: "#262626", color: "white", borderRadius: ".25vw" },
                    "&.Mui-selected:hover": { backgroundColor: "#262626", color: "white", borderRadius: ".25vw" },
                    "&:hover": { backgroundColor: "#262626", color: "white", borderRadius: ".25vw" },
                  },
                },
              },
            }}
            sx={{
              height: "2.3vw",
              minWidth: "10vw",
              fontFamily: "Poppins",
              fontWeight: 500,
              fontSize: ".8vw",
              color: "#fff",
              flexShrink: 0,
              "& .MuiOutlinedInput-notchedOutline": { border: ".025vw solid #595959", borderRadius: ".25vw" },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#595959" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: ".025vw solid #595959" },
            }}
          >
            <MenuItem value="" sx={{ backgroundColor: "#FFFFFF", color: "#262626" }}>All Events</MenuItem>
            {eventOptions.map((ev) => (
              <MenuItem key={ev._id} value={ev._id} sx={{ backgroundColor: "#FFFFFF", color: "#262626" }}>
                {capitalEachLetter(ev.eventTypeName)}
              </MenuItem>
            ))}
          </Select>
          <Calendar cursor="pointer" onClick={() => setIsCalendarOpen(true)} />
          <DatePickerComp
            startDate={draftStart}
            endDate={draftEnd}
            onChange={handleDateRangeChange}
            handleClear={handleDateClear}
            open={isCalendarOpen}
            handleClose={() => setIsCalendarOpen(false)}
            handleApply={handleDateApply}
          />
          <Box className="main-search-outer" sx={{ background: "white", minWidth: "18vw", flexShrink: 0 }}>
            <span>{getIcon("Search", "grey")}</span>
            <input
              type="text"
              placeholder="Search By client or Sales person"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
            />
            <button
              className="main-search-btn"
              style={{ background: "black", color: "white" }}
              onClick={() => (search === "" ? runSearch() : clearSearch())}
            >
              {search === "" ? "Search" : "Clear"}
            </button>
          </Box>
        </Box>
      </Box>

      {/* Tabs */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "0",
          flexShrink: 0,
          padding: "0 1.6vw",
          borderBottom: ".05vw solid #333",
          background: "#000",
        }}
      >
        {TABS.map((tab, i) => (
          <StyledTab key={tab} active={activeTab === i ? 1 : 0} onClick={() => setActiveTab(i)}>
            {tab}
          </StyledTab>
        ))}
      </Box>

      {/* Tab content */}
      <Box sx={{ padding: "1vw 1.6vw", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {activeTab === 0 && (
          <StyledTableContainer id="report-scrollable-table">
            <StyledTable>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) => <TableCell key={col.key}>{col.label}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading && Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key}>
                        <Skeleton variant="text" animation="wave" sx={{ fontSize: "1rem" }} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {!isLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={COLUMNS.length}>No designs recorded for this period.</TableCell>
                  </TableRow>
                )}
                {!isLoading && rows.map((row, i) => (
                  <TableRow key={`${row.orderNo || row.date}-${i}`}>
                    {COLUMNS.map((col) => {
                      let content;
                      if (col.key.startsWith("section:")) {
                        const group = SECTION_GROUPS.find((g) => `section:${g.label}` === col.key);
                        content = group ? (groupTotal(row.sectionCounts, group) || "-") : "-";
                      } else if (col.key === "date") {
                        content = row.date ? formatDate(row.date) : "-";
                      } else if (col.key === "totalDiamondWt") {
                        content = row.totalDiamondWt != null ? `${row.totalDiamondWt} ct` : "-";
                      } else if (col.key === "totalGoldWt") {
                        content = row.totalGoldWt != null ? `${row.totalGoldWt} g` : "-";
                      } else if (col.key === "mobile") {
                        content = formatMobile(row.mobile);
                      } else if (col.key === "salesPerson") {
                        content = row.salesPerson?.trim() || "-";
                      } else if (col.key === "clientName") {
                        content = getClientLabel(row) || "-";
                      } else {
                        content = row[col.key] || "-";
                      }
                      return <TableCell key={col.key}>{content}</TableCell>;
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </StyledTableContainer>
        )}

        {activeTab === 1 && (
          <AnalyticsTable
            columns={clientColumns}
            rows={analytics?.topClients || []}
            loading={analyticsLoading}
            emptyMsg="No client data for this period."
          />
        )}

        {activeTab === 2 && (
          <AnalyticsTable
            columns={stateColumns}
            rows={analytics?.topStates || []}
            loading={analyticsLoading}
            emptyMsg="No state data for this period."
          />
        )}

        {activeTab === 3 && (
          <AnalyticsTable
            columns={cityColumns}
            rows={analytics?.topCities || []}
            loading={analyticsLoading}
            emptyMsg="No city data for this period."
          />
        )}

        {activeTab === 4 && (
          <AnalyticsTable
            columns={categoryColumns}
            rows={analytics?.topCategories || []}
            loading={analyticsLoading}
            emptyMsg="No category data for this period."
          />
        )}
      </Box>
    </Box>
  );
};

export default ReportScreen;
